import {
    buildHierarchyFlatOrder,
    getChildren,
    getInheritedDeadline,
    getInheritedTimeline,
    getTaskLevel,
} from './taskHierarchyUtils';
import {
    formatDuration,
    getStatusDisplayMeta,
    getVisibleTaskTags,
    taskIsOffTrack,
} from './taskSchedulingUtils';
import { formatGroupHeaderTitle } from './taskGroupSummary';
import { getTaskTableVisibleColumns, TASK_TABLE_COLUMN_LABELS } from './taskTableColumns';
import { exportTableToPdf } from './exportTableToPdf';
import { getTaskPdfCellStyle, TASK_BADGE_COLUMNS } from './pdfCellStyles';
import {
    filterRowsByIds,
    formatPdfDate,
    formatPdfDateTime,
    formatPlainText,
} from './pdfTextUtils';

const PRIORITY_LABELS = {
    HIGH: 'High',
    MEDIUM: 'Medium',
    LOW: 'Low',
    '': 'None',
    NONE: 'None',
};

const COLUMN_WIDTH_WEIGHTS = {
    task: 3.6,
    owner: 1.3,
    status: 1.15,
    timeline: 1.5,
    deadline: 1.1,
    duration: 0.95,
    priority: 1,
    workstream: 1.5,
    tag: 1.15,
    projectPhase: 1.2,
    lastUpdated: 1.25,
    addedOn: 1.25,
};

const orderTasksForExport = (tasks, customOrder) => {
    const orderedIds = buildHierarchyFlatOrder(tasks, customOrder, null);
    const taskMap = new Map(tasks.map(task => [task.id, task]));
    return orderedIds.map(id => taskMap.get(id)).filter(Boolean);
};

const getOwnerName = (task, projectUsersAll) => {
    const userId = task?.assigned_to || task?.created_by;
    if (!userId) return '';
    const match = projectUsersAll?.find(user => user?.value?.user_id === userId);
    return formatPlainText(match?.value?.full_name || '');
};

const taskHasChildren = (task, allTasks) => getChildren(task?.id, allTasks).length > 0;

const getTagText = task =>
    getVisibleTaskTags(task)
        .map(formatPlainText)
        .filter(Boolean)
        .join(', ');

const getDeadlineValue = (task, allTasks) => {
    if (taskHasChildren(task, allTasks)) {
        return task.deadline || getInheritedDeadline(task, allTasks).deadline;
    }
    return task.deadline;
};

const getTaskCellValue = (task, columnKey, context) => {
    const { allTasks, projectUsersAll, selectedProject } = context;

    switch (columnKey) {
        case 'task': {
            const level = getTaskLevel(task.id, allTasks);
            const indent = level > 0 ? `${'  '.repeat(level)}- ` : '';
            return `${indent}${formatPlainText(task.task || '')}`;
        }
        case 'owner':
            return getOwnerName(task, projectUsersAll);
        case 'status':
            // Parents: show rolled-up label in muted grey (match screen), never a colored badge.
            return getStatusDisplayMeta(task.status, selectedProject)?.label || '';
        case 'timeline': {
            const timeline = getInheritedTimeline(task, allTasks);
            const start = formatPdfDate(timeline?.start_date);
            const end = formatPdfDate(timeline?.end_date);
            if (start && end) return `${start} - ${end}`;
            return start || end || '';
        }
        case 'deadline': {
            const deadlineValue = getDeadlineValue(task, allTasks);
            const dateText = formatPdfDate(deadlineValue);
            if (!dateText) return '';
            const offTrack = taskIsOffTrack({
                ...task,
                deadline: deadlineValue,
                end_date: task.end_date || getInheritedTimeline(task, allTasks)?.end_date,
            });
            if (offTrack && task.status !== 'COMPLETED' && task.status !== 'COMPLETE' && task.status !== 'CANCELLED') {
                return `! Off-track  ${dateText}`;
            }
            return dateText;
        }
        case 'duration':
            return formatDuration(task.duration);
        case 'priority':
            return PRIORITY_LABELS[task.priority] || PRIORITY_LABELS[''];
        case 'workstream':
            return formatPlainText(task.workstream_repr || '');
        case 'tag':
            return getTagText(task);
        case 'projectPhase':
            return formatPlainText(task.project_phase || '');
        case 'lastUpdated':
            return formatPdfDateTime(task.last_updated);
        case 'addedOn':
            return formatPdfDateTime(task.created);
        default:
            return '';
    }
};

export const exportTasksToPdf = async ({
    filteredData,
    projectName,
    selectedProject,
    projectUsersAll,
    allTaskData,
    isColumnVisible,
    columnOrder,
    customOrder = null,
    selectedTaskIds = null,
    filename = 'tasks.pdf',
}) => {
    const visibleColumnKeys = getTaskTableVisibleColumns({ columnOrder, isColumnVisible });
    const visibleColumns = visibleColumnKeys.map(key => ({
        key,
        label: TASK_TABLE_COLUMN_LABELS[key],
    }));

    const context = {
        allTasks: allTaskData || [],
        projectUsersAll,
        selectedProject,
    };

    let groupedSections = null;
    let rows = [];

    if (filteredData?.isGrouped) {
        groupedSections = (filteredData.groups || []).map(group => ({
            title: formatGroupHeaderTitle(group.groupName, group.tasks, allTaskData || group.tasks),
            rows: filterRowsByIds(orderTasksForExport(group.tasks || [], customOrder), selectedTaskIds),
            context,
        }));
    } else {
        const sectionTasks = filteredData?.data || filteredData || [];
        rows = filterRowsByIds(orderTasksForExport(sectionTasks, customOrder), selectedTaskIds);
    }

    await exportTableToPdf({
        title: 'Tasks',
        projectName,
        visibleColumns,
        rows,
        groupedSections,
        getCellValue: getTaskCellValue,
        getCellStyle: (row, columnKey, ctx) => getTaskPdfCellStyle(columnKey, row, ctx),
        columnWeights: COLUMN_WIDTH_WEIGHTS,
        badgeColumns: TASK_BADGE_COLUMNS,
        context,
        filename,
    });
};
